import { logger } from 'src/services';
import { create } from 'zustand';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { useAuthStore } from '../auth/useAuthStore';
import { getFixedT } from 'i18next';
import {
  createChatApi,
  CreateChatApiPayload,
  StartChatApiPayload,
  GetChatHistoryApiPayload,
  ChatMessage,
  startChatApi,
  getChatHistoryApi,
  SendResponseApiPayload,
  sendResponseApi,
  clearChatApi,
  ClearChatApiPayload,
  SendChatFeedbackApiPayload,
  sendChatFeedbackApi,
} from 'src/api';
import { Appointment } from 'src/models';

type Actions = {
  createChat: (payload: CreateChatApiPayload) => Promise<void>;
  startChat: (payload: StartChatApiPayload) => Promise<void>;
  getChatHistory: (payload: GetChatHistoryApiPayload) => Promise<void>;
  sendResponse: (
    payload: SendResponseApiPayload & {
      loadingMessage?: string;
      retry?: boolean;
    }
  ) => Promise<void>;
  regenerateResponse: (
    payload: Pick<SendResponseApiPayload, 'uuid' | 'chatToken'>
  ) => Promise<void>;
  sendChatFeedback: (payload: SendChatFeedbackApiPayload) => Promise<void>;
  markComplete: (
    payload: Pick<SendResponseApiPayload, 'uuid' | 'chatToken'>
  ) => Promise<void>;
  setTextToSpeech: (value: State['textToSpeech']) => void;
  setSpeechToText: (value: State['speechToText']) => void;
  setZoom: (value: number) => void;
  downloadTranscript: (payload: {
    messages: ChatMessage[];
    details: Appointment;
  }) => void;
  clearChat: (payload: ClearChatApiPayload) => Promise<void>;
};

const zoomLocalStorageKey = 'userSettings.zoom';
const ttsLocalStorageKey = 'userSettings.tts';
const sttLocalStorageKey = 'userSettings.stt';
const defaultZoom = 1;

type State = {
  defaultZoom: number;
  zoom: number;
  progress: number;
  complete: boolean;
  chat: {
    messages: ChatMessage[];
    loading?: boolean | string;
    error?: {
      message: string;
      lastAttempt: Pick<SendResponseApiPayload, 'content' | 'toolResponses'>;
    } | null;
  };
  textToSpeech: 'on' | 'off';
  speechToText: 'on' | 'off' | 'loading';
  smartReplies: string[];
  loading: boolean;
  error: string | null;
};

const useChatStore = create<State & Actions>((set) => ({
  defaultZoom,
  zoom: parseFloat(
    localStorage.getItem(zoomLocalStorageKey) || defaultZoom.toString()
  ),
  progress: 0,
  chat: {
    messages: [],
    loading: false,
    error: null,
  },
  textToSpeech: ['on'].includes(
    localStorage.getItem(ttsLocalStorageKey) as State['textToSpeech']
  )
    ? 'on'
    : 'off',
  speechToText: ['on', 'loading'].includes(
    localStorage.getItem(sttLocalStorageKey) as State['speechToText']
  )
    ? 'loading'
    : 'off',
  complete: false,
  smartReplies: [],
  loading: false,
  error: null,
  createChat: async ({ chatToken }) => {
    set({ loading: true, error: null });

    try {
      const response = await createChatApi({ chatToken });
      const newUuid = response.data.Chat.chat_uuid;
      const newPhase = response.data.Chat.phase;
      logger.info('Created chat: updating appointment uuid', {
        uuid: newUuid,
        phase: newPhase,
      });
      useAuthStore.setState({
        appointment: {
          ...useAuthStore.getState().appointment!,
          chat_phase: newPhase,
          chat_uuid: newUuid,
        },
      });
      useChatStore.getState().startChat({ uuid: newUuid });
      set({ progress: response.data.Chat.progress, loading: false });
    } catch (error) {
      const errorMessage = 'Failed to create chat';
      logger.error(errorMessage, {}, error as Error);
      toast.error(errorMessage);
      set({ error: errorMessage, loading: false });
    }
  },
  startChat: async ({ uuid }) => {
    // using both error to show chat like loading but global error
    set((state) => ({
      error: null,
      chat: { ...state.chat, loading: true, error: null },
    }));
    try {
      const response = await startChatApi({ uuid });
      set({
        error: null,
        chat: { messages: [response.data] },
      });
    } catch (error) {
      const errorMessage = 'Failed to start chat';
      logger.error(errorMessage, {}, error as Error);
      toast.error(errorMessage);
      set((state) => ({
        error: errorMessage,
        chat: { ...state.chat, loading: false, error: null },
      }));
    }
  },
  getChatHistory: async ({ chatToken }) => {
    set({ loading: true, error: null });
    try {
      const response = await getChatHistoryApi({ chatToken });
      const { progress, chat_message } = response.data;
      set({
        progress,
        chat: { messages: chat_message },
        loading: false,
      });
    } catch (error) {
      const errorMessage = 'Failed to get chat history';
      logger.error(errorMessage, {}, error as Error);
      toast.error(errorMessage);
      set({ error: errorMessage, loading: false });
    }
  },
  sendResponse: async ({
    uuid,
    content,
    timestamp,
    chatToken,
    loadingMessage,
    retry,
    toolResponses = [],
  }) => {
    // Optimistic render the chat message
    set((state) => {
      const newMessages =
        (content || toolResponses?.length > 0) && !retry
          ? [
              ...state.chat.messages,
              {
                role: 'user' as const,
                content,
                timestamp,
                classification: null,
                trace_id: `${Date.now()}_${uuid}`,
                tool_responses: toolResponses,
              },
            ]
          : state.chat.messages;
      return {
        smartReplies: [],
        chat: {
          ...state.chat,
          messages: newMessages,
          loading: loadingMessage || true,
          error: null,
        },
      };
    });

    try {
      const response = await sendResponseApi({
        uuid,
        content,
        timestamp,
        chatToken,
        toolResponses,
      });

      const {
        smart_replies,
        response: chatResponse,
        chat_phase,
        progress,
        chat_transition,
        chat_transition_message,
      } = response.data;

      const appointment = useAuthStore.getState().appointment!;
      if (chat_phase && chat_phase != appointment?.chat_phase) {
        useAuthStore.setState({
          appointment: {
            ...appointment,
            chat_phase,
            chat_completion_status: 'InProgress',
          },
        });
      }

      if (chatResponse) {
        set((state) => ({
          smartReplies: smart_replies || [],
          chat: {
            ...state.chat,
            messages: [...state.chat.messages, chatResponse],
            loading: false,
          },
          progress,
        }));
      } else if (chat_transition) {
        useChatStore.getState().sendResponse({
          uuid,
          content: '',
          timestamp: new Date().toISOString(),
          chatToken,
          loadingMessage: chat_transition_message,
        });
      }
    } catch (error) {
      const silentRetry = !retry;
      if (silentRetry) {
        logger.warn('Patient chat failed, silently retrying...');
        useChatStore.getState().sendResponse({
          uuid,
          content,
          toolResponses,
          timestamp,
          chatToken,
          loadingMessage,
          retry: true,
        });
      } else {
        const errorMessage = 'Failed to generate a response';
        logger.error(errorMessage, {}, error as Error);
        toast.error(errorMessage);
        set((state) => ({
          chat: {
            ...state.chat,
            loading: false,
            error: {
              message: errorMessage,
              lastAttempt: { content, toolResponses: toolResponses },
            },
          },
        }));
      }
    }
  },
  regenerateResponse: async ({ uuid, chatToken }) => {
    const { content, toolResponses } =
      useChatStore.getState().chat.error?.lastAttempt || {};
    await useChatStore.getState().sendResponse({
      uuid,
      content: content || '',
      timestamp: new Date().toISOString(),
      chatToken,
      toolResponses,
      retry: true,
    });
  },
  sendChatFeedback: async (payload) => {
    try {
      await sendChatFeedbackApi(payload);
      toast.success(getFixedT(null, 'vca')('feedback.submitSuccess'));
    } catch (error) {
      const errorMessage = getFixedT(null, 'vca')('feedback.submitError');
      logger.error(errorMessage, {}, error as Error);
      toast.error(errorMessage);
    }
  },
  markComplete: async ({ chatToken, uuid }) => {
    set({ loading: true });
    try {
      await sendResponseApi({
        chatToken,
        uuid,
        timestamp: new Date().toISOString(),
        content: '',
        isComplete: true,
      });
      set({ complete: true });
    } catch (error) {
      const errorMessage = getFixedT(null, 'vca')('chatCompletion.error');
      logger.error(errorMessage, {}, error as Error);
      toast.error(errorMessage);
    }
    set({ loading: false });
  },
  setTextToSpeech: (value) => {
    localStorage.setItem(ttsLocalStorageKey, value);
    set({ textToSpeech: value });
  },
  setSpeechToText: (value) => {
    localStorage.setItem(sttLocalStorageKey, value);
    set({ speechToText: value });
  },
  setZoom: (value) => {
    if (value < 0.5 || value > 1.5) {
      return;
    }
    localStorage.setItem(zoomLocalStorageKey, value.toFixed(1));
    set({ zoom: value });
  },
  downloadTranscript: ({ messages, details }) => {
    const blob = new Blob(
      [
        messages
          .filter((msg): msg is ChatMessage =>
            ['assistant', 'user'].includes(msg.role)
          )
          .map((msg) => {
            const sender =
              msg.role === 'assistant'
                ? 'Doctor'
                : `Patient (${[
                    details.patient_first_name,
                    details.patient_last_name,
                  ]
                    .filter(Boolean)
                    .join(' ')})`;
            return `${sender}:\n${msg.content}\n`;
          })
          .join('\n'),
      ],
      { type: 'text/plain' }
    );

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'chat_transcript.txt';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  },
  clearChat: async ({ chatToken, uuid }) => {
    await clearChatApi({ chatToken, uuid });
    set({
      chat: { messages: [], loading: false, error: null },
      smartReplies: [],
    });
    await useChatStore.getState().createChat({ chatToken });
  },
}));

export { useChatStore };
